/**
 * LeaveSummaryCard Component
 * Displays a summary card for a specific leave type with available and booked days
 */

import React from 'react';
import { Paper, Stack, Group, Text, ThemeIcon, Box, rem, Button } from '@mantine/core';
import { createStyles } from '@mantine/core';

const useStyles = createStyles((theme, { color }: { color: string }) => ({
    summaryCard: {
        // Gradient background for a vibrant but soft look
        background: `linear-gradient(135deg, ${theme.fn.rgba(color, 0.15)} 0%, ${theme.fn.rgba(theme.white, 0.5)} 100%)`,
        border: `1px solid ${theme.fn.rgba(color, 0.3)}`,
        padding: theme.spacing.md, // Reduced padding
        borderRadius: theme.radius.lg,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows.md,
            border: `1px solid ${color}`,
        },
    },
    iconWrapper: {
        backgroundColor: theme.white,
        color: color,
        boxShadow: `0 4px 12px ${theme.fn.rgba(color, 0.2)}`
    }
}));

interface LeaveSummaryCardProps {
    title: string;
    icon: React.ReactNode;
    iconColor: string;
    iconClassName?: string;
    available: number;
    booked: number;
    availableColor?: string;
    onApply?: () => void;
}

export const LeaveSummaryCard: React.FC<LeaveSummaryCardProps> = ({
    title,
    icon,
    iconColor,
    iconClassName,
    available,
    booked,
    availableColor = 'black',
    onApply,
}) => {
    const { classes, theme } = useStyles({ color: iconColor });

    return (
        <Paper className={classes.summaryCard} shadow="sm">
            <Group position="apart" align="flex-start" noWrap>
                <Stack spacing={0}>
                    <Text size="xs" weight={700} color="dimmed" transform="uppercase" style={{ letterSpacing: '0.5px' }}>
                        {title}
                    </Text>
                    <Group align="baseline" spacing={4}>
                        <Text size={rem(30)} weight={800} style={{ color: theme.fn.darken(iconColor, 0.2), lineHeight: 1.2 }}>
                            {available}
                        </Text>
                    </Group>
                    <Text size="xs" color="dimmed" weight={500}>
                        Available Balance
                    </Text>
                </Stack>

                <ThemeIcon
                    size={42}
                    radius="xl"
                    className={classes.iconWrapper}
                >
                    {React.cloneElement(icon as React.ReactElement, {
                        size: 24,
                        stroke: 1.5,
                        color: iconColor
                    })}
                </ThemeIcon>
            </Group>
            <Box mt="md">
                <Group position='apart' mb={2}>
                    <Text size="xs" weight={600} color={iconColor}>Usage</Text>
                    <Text size="xs" weight={700} color="dimmed">{booked} Days Booked</Text>
                </Group>
                <Box
                    sx={{
                        width: '100%',
                        height: 6,
                        backgroundColor: theme.white,
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: `1px solid ${theme.fn.rgba(iconColor, 0.2)}`
                    }}
                >
                    <Box
                        sx={{
                            width: `${Math.min((booked / (available + booked || 1)) * 100, 100)}%`,
                            height: '100%',
                            background: `linear-gradient(90deg, ${iconColor}, ${theme.fn.lighten(iconColor, 0.2)})`,
                            borderRadius: 8,
                        }}
                    />
                </Box>
                {onApply && (
                    <Group position="right" mt="xs">
                        <Button
                            variant="subtle"
                            size="xs"
                            compact
                            color={iconColor}
                            onClick={onApply}
                            sx={{
                                '&:hover': {
                                    backgroundColor: theme.fn.rgba(iconColor, 0.1),
                                }
                            }}
                        >
                            Apply {title}
                        </Button>
                    </Group>
                )}
            </Box>
        </Paper>
    );
};
